import {
  formatCurrency,
  formatDateTime,
  type CurrencyCode,
  type MemberRole,
  type MemberStatus,
  type OrderPaymentIssueResolution,
  type OrderStatus,
  type PaymentQueryStatus,
  type OrganizerStatus,
  type PayoutStatus,
  type PlatformAdminRole,
} from "@ticketnjoy/types";

export function money(amount: number, currency: CurrencyCode): string {
  return formatCurrency(amount, currency);
}

export function dateTime(value: string | null | undefined): string {
  return value ? formatDateTime(value) : "—";
}

export function dateOnly(value: string | null | undefined): string {
  return value ? formatDateTime(value, "tr", { dateStyle: "medium" }) : "—";
}

export function percent(value: number): string {
  return `%${value.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}`;
}

export const ORGANIZER_STATUS_LABELS: Record<OrganizerStatus, string> = {
  Active: "Aktif",
  PendingEmailVerification: "E-posta doğrulanmadı",
  Suspended: "Askıda",
  Deleted: "Silinmiş",
};

export function organizerStatusVariant(
  status: OrganizerStatus,
): "success" | "warning" | "destructive" | "secondary" {
  switch (status) {
    case "Active":
      return "success";
    case "Suspended":
      return "destructive";
    case "Deleted":
      return "secondary";
    default:
      return "warning";
  }
}

export const SHOP_STATUS_LABELS: Record<string, string> = {
  Active: "Aktif",
  Paused: "Duraklatıldı",
  Archived: "Arşivlendi",
};

export const ADMIN_ROLE_LABELS: Record<PlatformAdminRole, string> = {
  SuperAdmin: "Süper admin",
  Support: "Destek",
};

export const MEMBER_ROLE_LABELS: Record<MemberRole, string> = {
  Owner: "Sahip",
  Manager: "Yönetici",
  Editor: "Editör",
  Accountant: "Muhasebe",
  Gate: "Kapı görevlisi",
};

export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  Invited: "Davet bekliyor",
  Active: "Aktif",
  Disabled: "Pasif",
};

export function memberStatusVariant(status: MemberStatus): "success" | "warning" | "secondary" {
  switch (status) {
    case "Active":
      return "success";
    case "Invited":
      return "warning";
    default:
      return "secondary";
  }
}

export const PLATFORM_ACTION_LABELS: Record<string, string> = {
  "Admin.Login": "Admin girişi",
  "Admin.PasswordReset": "Admin şifresi belirlendi",
  "Admin.Invite": "Admin davet edildi",
  "Admin.InviteResend": "Admin daveti yeniden gönderildi",
  "Admin.RoleChange": "Admin rolü değişti",
  "Admin.Disable": "Admin pasif yapıldı",
  "Admin.Enable": "Admin tekrar aktif",
  "Admin.SessionsRevoke": "Admin oturumları kapatıldı",
  "EventReport.Regenerate": "Etkinlik raporu yeniden üretildi",
  "Organizer.FeeChange": "Komisyon / servis ücreti değişti",
  "Organizer.Suspend": "Hesap askıya alındı",
  "Organizer.Unsuspend": "Hesap tekrar açıldı",
  "Organizer.ImpersonationStart": "Organizatör olarak girildi",
  "Organizer.ImpersonationEnd": "Organizatör oturumu kapatıldı",
  "Organizer.ProfileUpdate": "Hesap profili düzenlendi",
  "Organizer.LegalInfoUpdate": "Fatura bilgileri düzenlendi",
  "Organizer.BankAccountChange": "Banka hesabı değiştirildi",
  "Organizer.PaymentSettingsChange": "Kart ödemesi ayarı değiştirildi",
  "PaymentProvider.Enable": "Ödeme sağlayıcısı açıldı",
  "PaymentProvider.Disable": "Ödeme sağlayıcısı kapatıldı",
  "PaymentProvider.CardFeeChange": "Kart komisyonu değiştirildi",
  "PaymentProvider.DefaultChange": "Varsayılan kart POS'u değiştirildi",
  "Order.Refund": "Sipariş iade edildi",
  "Order.Cancel": "Bekleyen sipariş iptal edildi",
  "Order.BuyerUpdate": "Alıcı bilgisi düzeltildi",
  "Order.NotificationResend": "Bilet bildirimi tekrar gönderildi",
  "Order.PaymentQuery": "Bankaya ödeme soruldu",
  "Order.PaymentConfirm": "Ödeme elle onaylandı, biletler kesildi",
  "Order.PaymentRefundWithoutTickets": "Ödeme biletsiz iade edildi",
  "Order.PaymentIssueDismiss": "Ödeme sorunu kapatıldı (bankada ödeme yok)",
  "Member.PasswordResetLink": "Kullanıcıya şifre sıfırlama linki gönderildi",
  "Member.InviteResend": "Kullanıcı daveti yeniden gönderildi",
  "Member.EmailVerify": "Hesap e-postası doğrulanmış sayıldı",
  "Member.Disable": "Kullanıcı pasif yapıldı",
  "Member.Enable": "Kullanıcı tekrar aktif",
  "Member.SessionsRevoke": "Kullanıcının oturumları kapatıldı",
  "Member.EmailChange": "Kullanıcı e-postası değişti",
  "Member.RoleChange": "Kullanıcı rolü değişti",
  "Member.OwnerTransfer": "Hesap sahipliği devredildi",
  "Payout.Create": "Ödeme oluşturuldu",
  "Payout.Update": "Ödeme düzenlendi",
  "Payout.Approve": "Ödeme onaylandı",
  "Payout.MarkPaid": "Ödeme gönderildi",
  "Payout.Reject": "Ödeme reddedildi",
  "Payout.Adjustment": "Bakiye düzeltmesi",
};

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  Pending: "Ödeme bekliyor",
  Paid: "Ödendi",
  Cancelled: "İptal",
  Expired: "Süresi doldu",
  Refunded: "İade edildi",
  PartiallyRefunded: "Kısmi iade",
};

export function orderStatusVariant(
  status: OrderStatus,
): "success" | "warning" | "destructive" | "secondary" | "outline" {
  switch (status) {
    case "Paid":
      return "success";
    case "Pending":
    case "PartiallyRefunded":
      return "warning";
    case "Refunded":
      return "destructive";
    default:
      return "secondary";
  }
}

/** Sipariş filtresinde gösterilen sağlayıcılar; `Dev` yalnız test ortamında oluşur. */
export const PAYMENT_PROVIDER_LABELS: Record<string, string> = {
  Param: "Param",
  Halkbank: "Halkbank",
  Isbank: "İş Bankası",
  Tami: "Tami",
  Garanti: "Garanti BBVA",
  Free: "Ücretsiz",
  Dev: "Test ödeme",
};

export const PAYMENT_QUERY_STATUS_LABELS: Record<PaymentQueryStatus, string> = {
  Paid: "Ödendi",
  NotPaid: "Ödenmedi",
  Refunded: "İade edilmiş",
  NotFound: "Bankada işlem yok",
  Unsupported: "Sorgu desteklenmiyor",
  Unknown: "Bilinmiyor (yanıt alınamadı)",
};

export const PAYMENT_ISSUE_RESOLUTION_LABELS: Record<OrderPaymentIssueResolution, string> = {
  confirmed: "Onaylandı, biletler kesildi",
  refunded: "Para biletsiz iade edildi",
  dismissed: "Bankada ödeme yok, kapatıldı",
};

export const ORDER_CHANNEL_LABELS: Record<string, string> = {
  Shop: "Mağaza",
  Embed: "Embed",
};

export function payoutStatusVariant(
  status: PayoutStatus,
): "default" | "success" | "warning" | "destructive" | "outline" {
  switch (status) {
    case "Requested":
      return "warning";
    case "Approved":
      return "default";
    case "Paid":
      return "success";
    case "Rejected":
      return "destructive";
    default:
      return "outline";
  }
}

/** Oran girişi: "3,5" veya "3.50" → 3.5; negatif, binlik ayraçlı ya da 2'den fazla ondalıklı ise null. */
export function parseDecimal(raw: string): number | null {
  const normalized = raw.trim().replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  return Number(normalized);
}

/** "1.234,5" veya "1234.50" → 1234.5; geçersizse null. */
export function parseAmount(raw: string): number | null {
  const s = raw.trim().replace(/\s|₺/g, "");
  const normalized = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  if (!/^-?\d+(\.\d{1,2})?$/.test(normalized)) return null;
  return Number(normalized);
}

const ISTANBUL_DAY = new Intl.DateTimeFormat("en-US", {
  timeZone: "Europe/Istanbul",
  year: "numeric",
  month: "numeric",
  day: "numeric",
});

/**
 * İstanbul'da bugünün takvim günü (`m` 1–12). Backend rapor aralıklarını Türkiye
 * gününe göre keser; admin başka saat dilimindeyse tarayıcı günü bir kayabilir.
 */
export function istanbulToday(): { y: number; m: number; d: number } {
  const parts = ISTANBUL_DAY.formatToParts(new Date());
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);
  return { y: get("year"), m: get("month"), d: get("day") };
}

/** `YYYY-MM-DD`; taşan değerler takvime göre düzelir (`d = 0` → önceki ayın son günü). */
export function isoDay(y: number, m: number, d: number): string {
  return new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
}

/** İstanbul'a göre bugün, `<input type="date">` formatında. */
export function todayIso(): string {
  const { y, m, d } = istanbulToday();
  return isoDay(y, m, d);
}
